/**
 * Điểm dùng asset duy nhất của render/UI.
 *
 * Mọi hình ảnh trong M2 đều là placeholder sinh bằng code theo ID trong
 * src/data/manifest.json. tools/validate-manifest.ts quét mọi lời gọi
 * layPlaceholder('...') trong src/render và src/ui để chắc ID nào dùng
 * cũng có trong manifest (và có hàm vẽ tương ứng).
 */
import { DANH_SACH_PLACEHOLDER, vePlaceholder } from './placeholder.js';
import { napAtlas } from './atlas.js';

export { DANH_SACH_PLACEHOLDER, veCanvasPlaceholder, dungAtlas } from './placeholder.js';
export { napAtlas } from './atlas.js';

/**
 * Nạp tài nguyên asset lúc khởi động (atlas đã bake nếu có).
 * Không bao giờ ném lỗi: thất bại → dùng hàm vẽ charforge trực tiếp.
 * Có timeout để dev không bake atlas vẫn boot nhanh.
 */
export async function khoiDongTaiNguyen(thoiGianChoMs = 2000): Promise<boolean> {
  try {
    return await Promise.race([
      napAtlas(),
      new Promise<boolean>((resolve) => setTimeout(() => resolve(false), thoiGianChoMs)),
    ]);
  } catch {
    return false;
  }
}

/**
 * Lấy placeholder theo ID manifest (data URL PNG sinh lúc chạy).
 * Ném lỗi nếu ID không có trong manifest — fail fast thay vì hình trống.
 */
export function layPlaceholder(id: string): string {
  if (!DANH_SACH_PLACEHOLDER[id]) {
    throw new Error(`Asset "${id}" không có trong manifest — kiểm tra src/data/manifest.json.`);
  }
  return vePlaceholder(id);
}

/** Ánh xạ tên kiểu khách (khach.json) → ID asset trong manifest. */
export function idAssetKhach(tenKieuKhach: string): string {
  switch (tenKieuKhach) {
    case 'Học sinh':
      return 'khach-hoc-sinh';
    case 'Dân văn phòng':
      return 'khach-van-phong';
    case 'Khách du lịch':
      return 'khach-du-lich';
    case 'Người lớn tuổi':
      return 'khach-lon-tuoi';
    case 'Đầu bếp đi chợ':
      return 'khach-dau-bep';
    case 'Khách đêm khuya':
      return 'khach-dem-khuya';
    default:
      throw new Error(`Kiểu khách lạ, chưa có asset: ${tenKieuKhach}.`);
  }
}

/** Ánh xạ mã mặt hàng (mat-hang.json, SP01–SP24) → ID icon trong manifest. */
export function idAssetMon(maMon: string): string {
  const mau = /^SP(\d{2})$/.exec(maMon);
  if (!mau?.[1]) {
    throw new Error(`Mã mặt hàng lạ, chưa có icon: ${maMon}.`);
  }
  const so = Number(mau[1]);
  if (so < 1 || so > 24) {
    throw new Error(`Mã mặt hàng ngoài khoảng SP01–SP24: ${maMon}.`);
  }
  return `mon-sp${mau[1]}`;
}

/** Ánh xạ mã nội thất (noi-that.json) → ID asset trong manifest. */
export function idAssetNoiThat(maNoiThat: string): string {
  switch (maNoiThat) {
    case 'KE':
      return 'ke-go';
    case 'TU_LANH':
      return 'tu-lanh';
    case 'QUAY_TN':
      return 'quay-thu-ngan';
    default:
      throw new Error(`Mã nội thất lạ, chưa có asset: ${maNoiThat}.`);
  }
}

/** Ánh xạ mã NPC (npc.json) → ID asset trong manifest. */
export function idAssetNpc(maNpc: string): string {
  const bang: Record<string, string> = {
    'ncc-uong': 'npc-chu-ba',
    'ncc-kho': 'npc-co-sau',
    'ncc-tuoi': 'npc-anh-tam',
    'kq-ba-tu': 'npc-ba-tu',
    'kq-anh-hai': 'npc-anh-hai',
    'kq-co-ba': 'npc-co-ba',
    'kq-chu-sau': 'npc-chu-sau',
    'kq-em-ut': 'npc-em-ut',
    'kq-di-nam': 'npc-di-nam',
    'hx-bac-tu': 'npc-bac-tu',
    'hx-chi-hong': 'npc-chi-hong',
    'hx-ong-chin': 'npc-ong-chin',
  };
  const id = bang[maNpc];
  if (!id) throw new Error(`Mã NPC lạ, chưa có asset: ${maNpc}.`);
  return id;
}

/** ID asset sàn theo mã khu. */
export function idAssetSanKhu(maKhu: string): string {
  switch (maKhu) {
    case 'pho-cho':
      return 'san-pho-cho';
    case 'bai-bien':
      return 'san-bai-bien';
    case 'cho-dem':
      return 'san-cho-dem';
    default:
      throw new Error(`Mã khu lạ, chưa có asset sàn: ${maKhu}.`);
  }
}
